import { ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import {
  GlassCard,
  SettingsPageHeader,
  SurfacePageBody,
  SurfacePageLayout,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { BUTTON_GHOST } from '@/shared/ui-class';
import {
  SEED_LINKED_IDENTITIES,
  type LinkedIdentitySeed,
} from '@/versions/v1/shared/settings-seed';
import { formatRelativeTime } from './format-utils';

const PROVIDER_LABELS: Record<string, string> = {
  'microsoft-entra-id': 'Microsoft Entra ID',
  google: 'Google',
};

function providerLabel(provider: string): string {
  return PROVIDER_LABELS[provider] ?? provider;
}

export function LinkedAccountsScene() {
  const [identities, setIdentities] = useState<LinkedIdentitySeed[]>(
    () => [...SEED_LINKED_IDENTITIES],
  );
  const hasGoogle = identities.some((identity) => identity.provider === 'google');

  const handleLinkGoogle = () => {
    if (hasGoogle) return;
    setIdentities((prev) => [
      ...prev,
      {
        id: `li-google-${Date.now()}`,
        provider: 'google',
        linkedAt: Date.now(),
        lastSignInAt: Date.now(),
      },
    ]);
  };

  return (
    <SurfacePageLayout width="narrow">
      <Commentable name="LinkedAccountsScene">
        <SurfacePageBody className="space-y-8" as="div">
          <SettingsPageHeader
            title="Linked accounts"
            description="Identity providers linked to your account for sign-in."
          />

          <GlassCard as="section" tone="strong" className="overflow-hidden">
            {identities.length === 0 ? (
              <div className="space-y-3 px-5 py-4 text-sm text-muted">
                <p>
                  No accounts are linked yet. Signing in with a workplace identity
                  provider will link it here.
                </p>
                <LinkGoogleButton disabled={hasGoogle} onClick={handleLinkGoogle} />
              </div>
            ) : (
              <div className="divide-y divide-[var(--glass-border)]">
                <ul className="divide-y divide-[var(--glass-border)]">
                  {identities.map((identity) => (
                    <li
                      key={identity.id}
                      className="flex items-start gap-3 px-5 py-3.5"
                    >
                      <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-zinc-400 dark:text-zinc-500" />
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-zinc-900 dark:text-zinc-100">
                          {providerLabel(identity.provider)}
                        </div>
                        <p className="mt-0.5 text-xs text-muted">
                          Linked {formatRelativeTime(identity.linkedAt)} · Last sign-in{' '}
                          {formatRelativeTime(identity.lastSignInAt)}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
                <div className="px-5 py-3.5">
                  <LinkGoogleButton disabled={hasGoogle} onClick={handleLinkGoogle} />
                </div>
              </div>
            )}
          </GlassCard>
        </SurfacePageBody>
      </Commentable>
    </SurfacePageLayout>
  );
}

function LinkGoogleButton({
  disabled,
  onClick,
}: {
  disabled: boolean;
  onClick: () => void;
}) {
  if (disabled) {
    return (
      <span className="text-sm text-muted">Google account linked.</span>
    );
  }

  return (
    <button type="button" onClick={onClick} className={BUTTON_GHOST}>
      Link Google
    </button>
  );
}
